import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Check, X, Link2, Unlink, RefreshCw, ExternalLink, AlertTriangle, ListChecks, Users, Tags, Calculator, FileText } from "lucide-react";
import { toast } from "sonner";
import XeroSalesHealth from "@/components/XeroSalesHealth";
import { XeroPayrollPanel } from "@/components/XeroPayrollPanel";

function SalesStat({ label, value, sub, warn }) {
  return (
    <div className={`p-3 border ${warn ? "border-red-300 bg-red-50" : "border-zinc-200 bg-zinc-50 dark:bg-[#151C27] dark:border-zinc-800"}`}>
      <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-black text-xl ${warn ? "text-red-600" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * Xero settings page — admin connects their Xero tenant, picks the org,
 * and runs sync operations. Audit log of every invoice push lives at the
 * bottom so they can drill from Fieldlink → Xero.
 */
export default function XeroIntegration() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [params, setParams] = useSearchParams();
  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [audit, setAudit] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactReport, setContactReport] = useState(null);
  const [contactQuery, setContactQuery] = useState('');
  const [contactError, setContactError] = useState('');
  const [tracking, setTracking] = useState([]);
  const [busy, setBusy] = useState({});
  const [salesSync, setSalesSync] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState('');
  const loadSettings = async () => {
    try { const {data} = await api.get('/xero/automatic-settings'); setSettings(data); setSettingsError(''); }
    catch { setSettingsError('Unable to load automatic syncing settings. Please refresh.'); }
  };
  const changeSetting = async (key, enabled) => {
    setBusy({settings: true});
    try {
      await api.put(key === 'payroll' ? '/xero/payroll/automatic' : '/xero/automatic-settings', key === 'payroll' ? {enabled} : {[key]: enabled});
      await loadSettings();
      toast.success('Sync preference saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Unable to save your preference'); }
    finally { setBusy({}); }
  };
  const [salesHealth, setSalesHealth] = useState(null);
  const [runDetails, setRunDetails] = useState(null);
  const [review, setReview] = useState(null);
  const reviewConflict = async (row) => {
    try { const {data} = await api.post(`/xero/sales/review/${row.kind}/${encodeURIComponent(row.id)}`, {}); setReview({...data, row}); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Unable to review this record'); }
  };
  const resolveConflict = async (choice) => {
    setBusy({sales: true});
    try {
      const {data} = await api.post(`/xero/sales/review/${review.row.kind}/${encodeURIComponent(review.row.id)}`, {choice, version: review.version});
      if (data.ok) { toast.success('Your chosen version has been saved'); setReview(null); }
      else toast.warning('This record still needs attention. Both versions have been preserved.');
      await loadSalesSync();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Unable to resolve safely'); }
    finally { setBusy({}); }
  };

  const isAdmin = ["admin", "super_admin"].includes(user.role);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/xero/status");
      setStatus(data);
      if (data.status === "pending_tenant") {
        // Need to pick a tenant
        try {
          const { data: c } = await api.get("/xero/connections");
          setConnections(c.connections || []);
          setTenantPickerOpen(true);
        } catch { /* ignore */ }
      }
    } catch { setStatus({ connected: false }); }
  };

  const loadAudit = async () => {
    try { const { data } = await api.get("/xero/audit"); setAudit(data || []); } catch { /* ignore */ }
  };
  const loadContacts = async () => {
    try { const { data } = await api.get("/xero/contacts/search", {params: {q: contactQuery}}); setContacts(data.items || []); setContactReport(data); setContactError(''); }
    catch { setContactError('Could not load contacts. Refresh or check the Xero connection.'); }
  };
  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      api.get('/xero/contacts/search', {params: {q: contactQuery}}).then(({data}) => {
        if (active) {setContacts(data.items || []); setContactReport(data); setContactError('');}
      }).catch(() => {if (active) setContactError('Could not load contacts. Refresh or check the Xero connection.');});
    }, 200);
    return () => {active = false; clearTimeout(timer);};
  }, [contactQuery]);
  useEffect(() => {
    if (!busy.contacts && contactReport?.sync?.state !== 'running') return;
    const timer = setInterval(loadContacts, 2500);
    return () => clearInterval(timer);
  }, [busy.contacts, contactReport?.sync?.state, contactQuery]);
  const loadTracking = async () => {
    try { const { data } = await api.get("/xero/tracking-categories"); setTracking(data || []); } catch { /* ignore */ }
  };
  const loadSalesSync = async (page = 1) => {
    try {
      const { data } = await api.get("/xero/sales/status", { params: { attention_page: page } });
      setSalesHealth(data);
      setSalesSync(data?.sales_sync_last || null);
    } catch { setSalesHealth({state: 'failed', attention_count: 0}); }
  };
  const retryDocument = async (row) => {
    setBusy({ sales: true });
    try {
      const { data } = await api.post(`/xero/sales/retry/${row.kind}/${encodeURIComponent(row.id)}`);
      if (data.ok) toast.success("Document sync checked successfully");
      else toast.warning("Document still requires attention; review the sync history.");
      await loadSalesSync(salesHealth?.attention_page || 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setBusy({}); }
  };
  const inspectRun = async (id, page) => {
    try {
      const { data } = await api.get(`/xero/sales/history/${encodeURIComponent(id)}`, { params: { page } });
      setRunDetails({ ...data, run_id: id });
    } catch { toast.error("Unable to load sync outcomes"); }
  };

  const runSalesSync = async () => {
    setBusy({ sales: true });
    try {
      const { data } = await api.post("/xero/sync/enabled");
      setSalesSync(data);
      const pulled = (data.quotes?.created || 0) + (data.quotes?.updated || 0)
        + (data.invoices?.created || 0) + (data.invoices?.updated || 0);
      const pushed = (data.pushed?.quotes || 0) + (data.pushed?.invoices || 0);
      if (data.state === "completed") toast.success(`Xero sync complete — ${pulled} pulled, ${pushed} pushed`);
      else toast.warning(`Xero sync ${data.state || "incomplete"} — review the sync history.`);
      await loadSalesSync();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sales sync failed");
    } finally { setBusy({}); }
  };

  useEffect(() => {
    loadStatus();
    loadSettings();
    loadAudit();
    loadContacts();
    loadTracking();
    loadSalesSync();
  }, []);

  useEffect(() => {
    if (!isAdmin || !status?.connected) return;
    const timer = setInterval(() => loadSalesSync(salesHealth?.attention_page || 1), 15000);
    return () => clearInterval(timer);
  }, [isAdmin, status?.connected, salesHealth?.attention_page]);

  useEffect(() => {
    const step = params.get("step");
    const err = params.get("error");
    const xeroErr = params.get("xero_error");
    const xeroErrDesc = params.get("xero_error_desc");
    if (xeroErr) {
      // Friendly bounce-back from our /xero/callback when Xero itself
      // returned an error param (e.g. invalid_scope when the Fieldlink
      // Xero app doesn't have Payroll enabled).
      toast.error(xeroErrDesc || `Xero: ${xeroErr}`, { duration: 12000 });
      setParams({}, { replace: true });
    } else if (err) {
      toast.error(`Xero connect failed: ${err}`);
      setParams({}, { replace: true });
    } else if (step === "tenant") {
      // tenant picker will be opened by loadStatus
      setParams({}, { replace: true });
    } else if (step === "connected") {
      toast.success("Connected to Xero");
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const connect = ({ withPayroll = true } = {}) => {
    // Use a full page navigation so cookies travel for the OAuth callback.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const suffix = withPayroll ? "?include_payroll=true" : "";
    window.location.href = `${base}/api/xero/connect${suffix}`;
  };

  const reauthoriseXero = () => {
    if (!isAdmin) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${base}/api/xero/reauthorise`;
  };

  const pickTenant = async (c) => {
    setBusy({ tenant: true });
    try {
      await api.post("/xero/select-tenant", { tenant_id: c.tenantId, connection_id: c.id });
      toast.success(`Connected to ${c.tenantName}`);
      setTenantPickerOpen(false);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tenant select failed");
    } finally { setBusy({}); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Fieldlink org from Xero? You can reconnect any time.")) return;
    setBusy({ disconnect: true });
    try {
      await api.post("/xero/disconnect");
      toast.success("Disconnected from Xero");
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally { setBusy({}); }
  };

  const sync = async (kind) => {
    setBusy({ [kind]: true });
    try {
      const { data } = await api.post(`/xero/sync/${kind === "contacts" ? "contacts" : "tracking-categories"}`);
      if (data.ok === false) toast.error(data.error || 'Contact sync incomplete. Retry shortly.');
      else toast.success(`Synced ${data.count} ${kind}`);
      if (kind === "contacts") await loadContacts(); else await loadTracking();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      if (kind === "contacts") await loadContacts();
    } finally { setBusy({}); }
  };

  return (
    <div className="p-4 lg:p-10 w-full" data-testid="xero-integration-page">
      <PageHeader
        tag="/ integration"
        title="Xero Accounting"
        subtitle="Connect once. Choose what stays in sync."
      />

      <ConnectionPanel
        status={status}
        isAdmin={isAdmin}
        busy={busy}
        onConnect={connect}
        onDisconnect={disconnect}
      />

      <section className="hard-card mt-6 p-5 md:p-8" aria-label="Automatic syncing">
        <h2 className="font-display text-lg font-bold">Automatic syncing</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4">Choose what Fieldlink keeps up to date with Xero.</p>
        {settingsError && <p role="alert">{settingsError}</p>}
        {[
          ['invoices', 'Invoices', 'Automatically sync invoices between Fieldlink and Xero.'],
          ['authorise_sent', 'Approve sent invoices in Xero', 'When you mark an invoice as Sent in Fieldlink, set it to Authorised (approved) in Xero. Off keeps it as a draft in Xero for you to approve there.'],
          ['quotes', 'Quotes', 'Automatically sync quotes between Fieldlink and Xero.'],
          ['contacts', 'Contacts', 'Keep contacts up to date between Fieldlink and Xero.'],
          ['payroll', 'Payroll hours', 'Send payroll hours to Xero when you process payroll.'],
        ].map(([key, label, description]) => (
          <div key={key} className="flex items-center justify-between gap-5 border-t py-5">
            <div>
              <label htmlFor={`sync-${key}`} className="font-semibold">{label}</label>
              <p id={`sync-${key}-description`} className="text-sm text-zinc-500 mt-1">{description}</p>
              {key === 'contacts' && <p className="text-xs text-zinc-500 mt-1">Imports contacts from Xero. Changes in Fieldlink are not sent back.</p>}
              {key === 'authorise_sent' && settings?.invoices !== true && <p className="text-xs text-orange-700 mt-1">Turn on Invoices sync first.</p>}
              {settings && settings[key] == null && key !== 'payroll' && <p className="text-xs text-orange-700 mt-1">Existing behaviour retained. Choose On to enable automatic syncing or Off to stop it.</p>}
              {key === 'payroll' && !status?.payroll_ready && <p className="text-xs text-orange-700 mt-1">Connect Payroll NZ and complete employee mappings in Advanced.</p>}
            </div>
            <button type="button" id={`sync-${key}`} role="switch" aria-label={label} aria-describedby={`sync-${key}-description`}
              aria-checked={settings?.[key] === true} disabled={!isAdmin || !status?.connected || !settings || !!Object.keys(busy).length || (key === 'payroll' && !status?.payroll_ready && !settings?.payroll) || (key === 'authorise_sent' && settings?.invoices !== true)}
              onClick={() => changeSetting(key, settings?.[key] !== true)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 ${settings?.[key] === true ? 'bg-[#FF5F15] text-white' : 'bg-zinc-100 text-zinc-700'}`}>
              {settings?.[key] === true ? 'On' : settings?.[key] === false ? 'Off' : 'Choose'}
            </button>
          </div>
        ))}
        <div className="border-t pt-5 flex flex-wrap items-center justify-between gap-4">
          <div role="status" aria-live="polite">
            <p className="font-semibold">{busy.sales || salesHealth?.state === 'running' ? 'Syncing' : settingsError || salesHealth?.connection_error || (settings?.contacts && contactReport?.sync?.state === 'incomplete') || salesHealth?.attention_count > 0 || ['failed', 'partial'].includes(salesHealth?.state) || (salesSync && !salesSync.ok) ? 'Needs attention' : salesHealth?.last_success_at ? 'Up to date' : status?.connected ? 'Connected' : 'Disconnected'}</p>
            {salesHealth?.connection_error && <p className="text-sm text-orange-700">Xero connection expired. Reconnect to continue syncing.</p>}
            <p className="text-sm text-zinc-500">Last successful sync: {salesHealth?.last_success_at ? new Date(salesHealth.last_success_at).toLocaleString() : 'Not yet'} · {salesHealth?.attention_count || 0} issues</p>
            {salesSync && <p className="text-sm">{salesSync.ok ? `${(salesSync.outcomes?.succeeded || 0) + (salesSync.contacts?.synced || 0)} records synced` : 'Some records need attention. Open Advanced for details.'}</p>}
          </div>
          <Button onClick={runSalesSync} disabled={!isAdmin || !status?.connected || !settings || !!Object.keys(busy).length || salesHealth?.state === 'running'} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="xero-sales-sync-btn">
            {busy.sales ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}{busy.sales ? 'Syncing…' : 'Sync Now'}
          </Button>
        </div>
      </section>

      {status?.connected && !status?.payroll_ready && (
        <div className="mt-4 border-l-4 border-[#FF5F15] bg-orange-50 dark:bg-orange-950/20 p-3 rounded-sm flex items-start gap-3" data-testid="xero-payroll-reconnect-banner">
          <AlertTriangle className="w-5 h-5 text-[#FF5F15] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-sm">Payroll permissions need authorisation</div>
            <div className="text-[11px] text-zinc-600 mt-0.5">Xero Accounting is connected. Reauthorise Xero to grant Payroll NZ permissions for the same Xero organisation. Your mappings, settings and export history will be preserved.</div>
          </div>
          <Button onClick={reauthoriseXero} disabled={!isAdmin} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[10px] h-8">
            <RefreshCw className="w-3 h-3 mr-1" /> Reauthorise Xero
          </Button>
        </div>
      )}

      {status?.connected && salesHealth?.connection_error === 'connection_expired' && (
        <div className="mt-4 border-l-4 border-[#FF5F15] bg-orange-50 dark:bg-orange-950/20 p-3 rounded-sm flex items-start gap-3" data-testid="xero-reauthorise-banner">
          <AlertTriangle className="w-5 h-5 text-[#FF5F15] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-sm">Xero needs reauthorisation</div>
            <div className="text-[11px] text-zinc-600 mt-0.5">Your Xero sign-in has expired, so syncing has paused. Reauthorise to reconnect the same Xero organisation in one step — no need to disconnect. Your mappings, settings and history are preserved.</div>
          </div>
          <Button onClick={reauthoriseXero} disabled={!isAdmin} data-testid="xero-reauthorise-btn" className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[10px] h-8">
            <RefreshCw className="w-3 h-3 mr-1" /> Reauthorise Xero
          </Button>
        </div>
      )}

      {status?.connected && (
        <details className="mt-8"><summary className="cursor-pointer font-semibold py-3">Advanced / Sync Details</summary>
        <Tabs defaultValue="sales" className="mt-4">
          <TabsList className="rounded-sm bg-zinc-100 dark:bg-[#151C27] p-1 h-auto">
            <TabsTrigger value="sales" data-testid="xero-tab-sales" className="rounded-sm text-[12px] font-semibold">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Quotes & invoices
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="xero-tab-contacts" className="rounded-sm text-[12px] font-semibold">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Contacts <span className="ml-1.5 text-zinc-500">{contactReport?.cached_eligible ?? "—"}</span>
            </TabsTrigger>
            <TabsTrigger value="tracking" data-testid="xero-tab-tracking" className="rounded-sm text-[12px] font-semibold">
              <Tags className="w-3.5 h-3.5 mr-1.5" /> Tracking categories <span className="ml-1.5 text-zinc-500">{tracking.length}</span>
            </TabsTrigger>
            <TabsTrigger value="payroll" data-testid="xero-tab-payroll" className="rounded-sm text-[12px] font-semibold">
              <Calculator className="w-3.5 h-3.5 mr-1.5" /> Payroll (NZ)
              {!status?.payroll_ready && <span className="ml-1.5 text-[9px] font-bold text-[#FF5F15]">•</span>}
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="xero-tab-audit" className="rounded-sm text-[12px] font-semibold">
              <ListChecks className="w-3.5 h-3.5 mr-1.5" /> Push log <span className="ml-1.5 text-zinc-500">{audit.length}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sales" className="mt-4">
            <div className="hard-card p-5" data-testid="xero-sales-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display font-bold uppercase tracking-tight">Two-way quotes & invoices sync</div>
                  <p className="text-xs text-zinc-600 mt-1 max-w-lg">
                    Imports Xero quotes and sales invoices and pushes eligible linked changes.
                    Number collisions and conflicting edits are held for review.
                    Linked edits also request an automatic sync when saved.
                  </p>
                </div>
              </div>

              {salesSync && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="xero-sales-last-result">
                  <SalesStat label="Quotes pulled" value={(salesSync.quotes?.created || 0) + (salesSync.quotes?.updated || 0)} sub={`${salesSync.quotes?.created || 0} new · ${salesSync.quotes?.linked || 0} linked`} />
                  <SalesStat label="Invoices pulled" value={(salesSync.invoices?.created || 0) + (salesSync.invoices?.updated || 0)} sub={`${salesSync.invoices?.created || 0} new · ${salesSync.invoices?.linked || 0} linked`} />
                  <SalesStat label="Pushed to Xero" value={(salesSync.pushed?.quotes || 0) + (salesSync.pushed?.invoices || 0)} sub={`${salesSync.pushed?.quotes || 0} quotes · ${salesSync.pushed?.invoices || 0} invoices`} />
                  <SalesStat label="Needs attention" value={(salesSync.outcomes?.failed || 0) + (salesSync.outcomes?.conflict || 0) + (salesSync.errors?.length || 0)} sub={salesSync.synced_at ? `last sync ${new Date(salesSync.synced_at).toLocaleString("en-NZ")}` : ""} warn={salesSync.state === "partial" || salesSync.state === "failed"} />
                </div>
              )}
              <XeroSalesHealth report={salesHealth} busy={!!busy.sales} onRefresh={loadSalesSync}
                onRetry={retryDocument} onReview={isAdmin ? reviewConflict : null} onInspect={inspectRun} details={runDetails} />
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <div className="mb-3 space-y-2 text-sm">
              <p>{contactReport?.total_eligible == null ? 'Eligible total not yet verified' : `${contactReport.total_eligible} eligible at last successful sync`} · {contactReport?.cached_eligible ?? 0} available locally</p>
              <p className="text-xs text-muted-foreground">Last successful sync: {contactReport?.last_success_at ? new Date(contactReport.last_success_at).toLocaleString() : 'Never'} · Latest run: {contactReport?.sync?.synced ?? 0} synced / {contactReport?.sync?.fetched ?? 0} fetched</p>
              {(busy.contacts || contactReport?.sync?.state === 'running') && <p role="status">Syncing contacts…</p>}
              {contactReport?.sync?.state !== 'completed' && <p role="status" className="text-orange-700 dark:text-orange-300">{contactReport?.sync?.error || 'Contact discovery is not yet complete. Sync to verify all eligible contacts.'}{contactReport?.sync?.retry_at && ` Retry after ${new Date(contactReport.sync.retry_at).toLocaleString()}.`}</p>}
              {contactError && <p role="alert">{contactError}</p>}
              <input aria-label="Search synced Xero contacts" className="w-full rounded border bg-background px-3 py-2 focus:ring-2 focus:ring-[#FF5F15]" placeholder="Search name, email or contact number" value={contactQuery} onChange={e => setContactQuery(e.target.value)} />
              <p className="text-xs text-muted-foreground">Showing up to 25 matches{contactReport?.has_more ? ' — refine your search for more.' : '.'}</p>
            </div>
            <SyncPanel
              title="Invoice contacts"
              desc="All active contacts are eligible, including suppliers and contacts not yet classified as customers. Archived contacts cannot be selected. Mappings always use the Xero ContactID."
              onSync={() => sync("contacts")}
              busy={busy.contacts || contactReport?.sync?.state === "running"}
              testIdSync="xero-sync-contacts"
              rows={contacts}
              empty="No contacts synced yet. Hit Sync to pull them."
              renderRow={(c) => (
                <div className="flex items-center justify-between py-2 border-b border-zinc-200 dark:border-zinc-800 last:border-0">
                  <div>
                    <div className="font-semibold text-[13px]">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{[c.email, c.contact_number].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {c.is_customer && <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">customer</span>}
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="tracking" className="mt-4">
            <SyncPanel
              title="Tracking categories"
              desc="Pull your Xero tracking categories (job codes / cost centres) so invoice line items can be tagged with the right segment automatically."
              onSync={() => sync("tracking")}
              busy={busy.tracking}
              testIdSync="xero-sync-tracking"
              rows={tracking}
              empty="No tracking categories synced. Hit Sync if you use them."
              renderRow={(t) => (
                <div className="py-2 border-b border-zinc-200 dark:border-zinc-800 last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-[13px]">{t.name}</div>
                    <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">{(t.options || []).length} options</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(t.options || []).slice(0, 8).map((o) => (
                      <span key={o.id} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">{o.name}</span>
                    ))}
                  </div>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="payroll" className="mt-4">
            <XeroPayrollPanel
              payrollReady={!!status?.payroll_ready}
              onReconnectNeeded={reauthoriseXero}
              canReauthorise={isAdmin}
            />
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <div className="hard-card p-5" data-testid="xero-audit-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold text-[14px]">Recent invoice pushes</div>
                  <div className="text-[12px] text-zinc-600 dark:text-zinc-400">Recent document outcomes. The Quotes & Invoices tab includes full sync history and safe error categories.</div>
                </div>
                <Button size="sm" variant="outline" onClick={loadAudit} className="rounded-sm">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                </Button>
              </div>
              {audit.length === 0 ? (
                <EmptyState title="Nothing pushed yet" description="Open any approved invoice and hit Push to Xero." />
              ) : (
                <div>
                  {audit.map((a) => (
                    <div key={a.id} className="py-2.5 border-b border-zinc-200 dark:border-zinc-800 last:border-0" data-testid={`xero-audit-${a.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {["success", "succeeded"].includes(a.status)
                            ? <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            : <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />}
                          <span className="font-semibold text-[13px]">{["success", "succeeded"].includes(a.status) ? "Succeeded" : a.status}</span>
                          <span className="text-[11px] font-mono text-zinc-500">{(a.pushed_at || "").slice(0, 16).replace("T", " ")}</span>
                        </div>
                        {a.xero_invoice_url && (
                          <a href={a.xero_invoice_url} target="_blank" rel="noreferrer"
                             className="text-[11px] font-bold text-[#FF5F15] hover:underline inline-flex items-center gap-1">
                            View in Xero <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">FL invoice {a.fieldlink_invoice_id}</div>
                      {(a.category || a.error) && <div className="text-[12px] text-red-600 dark:text-red-400 mt-1">{a.category ? a.category.replaceAll("_", " ") : "Legacy sync error — review required"}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs></details>
      )}

      <Dialog open={!!review} onOpenChange={open => { if (!open && !busy.sales) setReview(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Choose which version to keep</DialogTitle><DialogDescription>Review both versions. A copy of each is saved before resolving an editable draft.</DialogDescription></DialogHeader>
          {review && <>
            <div className="grid gap-4 sm:grid-cols-2">
              {[['Fieldlink', review.fieldlink], ['Xero', review.xero]].map(([label, version]) => <div key={label} className="border rounded p-4">
                <h3 className="font-semibold">{label}</h3>
                <p>Status: {version.status || version.Status}</p>
                <p>Total: {version.total ?? version.Total}</p>
                <p>Reference: {version.reference || version.Reference || 'None'}</p>
                <p>Date: {version.issue_date || version.Date || 'None'}</p>
                {(version.line_items || version.LineItems || []).map((line, i) => <p key={i} className="text-sm mt-2">{line.description || line.Description} · {line.qty ?? line.Quantity} × {line.unit_price ?? line.UnitAmount}</p>)}
              </div>)}
            </div>
            {review.can_resolve ? <div className="flex flex-wrap gap-3">
              <Button disabled={busy.sales} onClick={() => resolveConflict('fieldlink')}>Keep Fieldlink version</Button>
              <Button disabled={busy.sales} variant="outline" onClick={() => resolveConflict('xero')}>Keep Xero version</Button>
            </div> : <p role="status">This record cannot be replaced safely. Review it with your accounts administrator; paid, sent and linked payment records need their existing correction workflow.</p>}
          </>}
        </DialogContent>
      </Dialog>

      <TenantPicker
        open={tenantPickerOpen}
        connections={connections}
        busy={busy.tenant}
        onPick={pickTenant}
        onClose={() => setTenantPickerOpen(false)}
      />
    </div>
  );
}

function ConnectionPanel({ status, isAdmin, busy, onConnect, onDisconnect }) {
  if (!status) {
    return (
      <div className="hard-card p-6 flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin" /> Checking connection…
      </div>
    );
  }
  const connected = status.connected;
  return (
    <div className="hard-card p-6" data-testid="xero-connection-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-sm flex items-center justify-center text-white font-bold text-lg ${
            connected ? "bg-[#13B5EA]" : "bg-zinc-400 dark:bg-zinc-700"
          }`}>
            X
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display font-black text-xl uppercase tracking-tight">Xero Accounting</span>
              <span
                data-testid="xero-status-badge"
                className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${
                  connected
                    ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                    : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                }`}
              >
                {connected ? "Connected" : "Disconnected"}
              </span>
            </div>
            {connected ? (
              <div className="text-[12.5px] text-zinc-700 dark:text-zinc-300 mt-1">
                Organisation: <span className="font-bold">{status.tenant_name}</span>
                {status.connected_at && (
                  <span className="text-zinc-500"> · linked {(status.connected_at || "").slice(0, 10)}</span>
                )}
              </div>
            ) : (
              <div className="text-[12.5px] text-zinc-700 dark:text-zinc-300 mt-1">
                Connect Xero, then choose which features sync automatically.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!connected && isAdmin && (
            <Button
              data-testid="xero-connect-btn"
              onClick={onConnect}
              className="rounded-sm bg-[#13B5EA] hover:bg-[#0d99c8] text-white font-bold"
            >
              <Link2 className="w-4 h-4 mr-1.5" /> Connect to Xero
            </Button>
          )}
          {connected && isAdmin && (
            <Button
              data-testid="xero-disconnect-btn"
              variant="outline"
              onClick={onDisconnect}
              disabled={busy.disconnect}
              className="rounded-sm"
            >
              {busy.disconnect ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Unlink className="w-4 h-4 mr-1.5" />}
              Disconnect
            </Button>
          )}
          {!isAdmin && (
            <div className="text-[11px] text-zinc-600 dark:text-zinc-400 font-mono uppercase tracking-widest">
              Admin only
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SyncPanel({ title, desc, onSync, busy, testIdSync, rows, renderRow, empty }) {
  return (
    <div className="hard-card p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="font-semibold text-[14px]">{title}</div>
          <div className="text-[12px] text-zinc-600 dark:text-zinc-400 max-w-xl">{desc}</div>
        </div>
        <Button size="sm" onClick={onSync} disabled={busy} data-testid={testIdSync} className="rounded-sm bg-[#13B5EA] hover:bg-[#0d99c8] text-white">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Sync from Xero
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="Nothing synced yet" description={empty} />
      ) : (
        <div>{rows.map((r) => <div key={r.xero_contact_id || r.id || r.xero_id}>{renderRow(r)}</div>)}</div>
      )}
    </div>
  );
}

function TenantPicker({ open, connections, busy, onPick, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="xero-tenant-picker">
        <DialogHeader>
          <DialogTitle>Pick the Xero organisation</DialogTitle>
          <DialogDescription>
            Your Xero account has access to multiple organisations. Choose the one we should push Fieldlink invoices into.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {connections.length === 0 && (
            <div className="text-[13px] text-zinc-600 dark:text-zinc-400">
              No tenants returned from Xero. Make sure your Xero user has access to at least one organisation, then reconnect.
            </div>
          )}
          {connections.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`xero-tenant-${c.tenantId}`}
              onClick={() => onPick(c)}
              disabled={busy}
              className="w-full text-left p-3 rounded-sm border-2 border-zinc-200 dark:border-zinc-800 hover:border-[#13B5EA] disabled:opacity-50 transition-colors"
            >
              <div className="font-semibold text-[14px]">{c.tenantName}</div>
              <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">{c.tenantType}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
